import React from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { useBilling } from "../contexts/BillingContext";
import { apiRequest, ApiError } from "../api/client";
import { FloatingToast } from "../components/common/FloatingToast";

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message || fallback;
  if (error instanceof Error) return error.message || fallback;
  return fallback;
}

export default function AccountPage() {
  const { token, user, logout } = useAuth();
  const { snapshot, startCheckout, openPortal } = useBilling();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [purgeModalOpen, setPurgeModalOpen] = React.useState(false);
  const [purgeCurrentPassword, setPurgeCurrentPassword] = React.useState("");
  const [purgeConfirmText, setPurgeConfirmText] = React.useState("");
  const [purgeLoading, setPurgeLoading] = React.useState(false);
  const [passwordSaving, setPasswordSaving] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [upgradeLoading, setUpgradeLoading] = React.useState<null | "month" | "year">(null);
  const [message, setMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null);

  const isFreePlan = Boolean(snapshot && !snapshot.isBypassed && snapshot.plan.code === "free");
  const isTrialing = Boolean(snapshot?.subscription?.status === "trialing");
  const isBypassed = Boolean(snapshot?.isBypassed);
  const hasStripeSubscription = Boolean(snapshot?.subscription?.provider === "stripe" && !isBypassed);
  const hasStripeProSubscription = Boolean(
    hasStripeSubscription &&
      snapshot?.subscription?.planCode === "pro" &&
      (snapshot?.subscription?.status === "trialing" ||
        snapshot?.subscription?.status === "active" ||
        snapshot?.subscription?.status === "past_due")
  );
  const hasCancelScheduled = Boolean(hasStripeSubscription && snapshot?.subscription?.cancelAtPeriodEnd);
  const hasStripeTrialingSubscription = Boolean(hasStripeSubscription && snapshot?.subscription?.status === "trialing");
  const isPaidPro = Boolean(snapshot && !snapshot.isBypassed && snapshot.plan.code === "pro" && !isTrialing);
  const purgeConfirmMatches = purgeConfirmText.trim() === "BORRAR TODOS MIS DATOS";
  const canSubmitPurge = Boolean(purgeCurrentPassword.trim() || purgeConfirmMatches);

  React.useEffect(() => {
    if (!purgeModalOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !purgeLoading) {
        setPurgeModalOpen(false);
      }
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [purgeModalOpen, purgeLoading]);

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (!token) return;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "Completa los tres campos de contraseña." });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: "error", text: "La nueva contraseña debe tener al menos 8 caracteres." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "La confirmación no coincide con la nueva contraseña." });
      return;
    }
    setPasswordSaving(true);
    try {
      await apiRequest<void>("/auth/password", {
        method: "PUT",
        token,
        body: { currentPassword, newPassword },
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage({ type: "success", text: "Contraseña actualizada correctamente." });
    } catch (error) {
      setMessage({ type: "error", text: extractErrorMessage(error, "No se pudo actualizar la contraseña.") });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleOpenPortal = async (intent: "manage" | "cancel" = "manage") => {
    setMessage(null);
    setPortalLoading(true);
    try {
      await openPortal();
      if (intent === "cancel") {
        setMessage({
          type: "success",
          text: "Te hemos abierto el área privada de facturación. Allí puedes solicitar la baja de Pro (cancelación al final del periodo).",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: extractErrorMessage(error, "No se pudo abrir el portal de facturación.") });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (interval: "month" | "year") => {
    setMessage(null);
    setUpgradeLoading(interval);
    try {
      await startCheckout(interval);
    } catch (error) {
      setMessage({ type: "error", text: extractErrorMessage(error, "No se pudo abrir el checkout.") });
    } finally {
      setUpgradeLoading(null);
    }
  };

  const handlePurgeData = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (!token) return;
    if (!canSubmitPurge) {
      setMessage({
        type: "error",
        text: "Confirma con contraseña actual o con el texto exacto BORRAR TODOS MIS DATOS.",
      });
      return;
    }

    setPurgeLoading(true);
    try {
      await apiRequest("/account/data", {
        method: "DELETE",
        token,
        body: {
          currentPassword: purgeCurrentPassword.trim() || undefined,
          confirmText: purgeConfirmText.trim() || undefined,
        },
      });
      logout();
      navigate("/auth/login", { replace: true });
    } catch (error) {
      setMessage({ type: "error", text: extractErrorMessage(error, "No se pudo borrar tus datos.") });
    } finally {
      setPurgeLoading(false);
    }
  };

  return (
    <DashboardLayout active="account">
      <div className="bg-white border-b border-gray-200 px-4 py-4 lg:px-8 lg:py-6">
        <h1 className="text-2xl font-bold text-gray-900">Mi cuenta</h1>
        <p className="text-sm text-gray-600 mt-1">Gestiona tu acceso, tus cobros y la suscripción.</p>
      </div>
      <div className="setup-content account-page-content">
        <div className="account-page-grid">
          <FloatingToast
            message={message?.text ?? null}
            tone={message?.type === "error" ? "error" : "success"}
            onClear={() => setMessage(null)}
          />

          <div className="account-page-panels">
            <section className="panel account-panel">
              <h2 className="panel-title">Acceso y seguridad</h2>
              <p className="panel-subtitle">Email de acceso: {user?.email ?? "-"}</p>

              <form onSubmit={handleChangePassword} className="account-password-form">
                <label className="account-form-label">
                  <span>Contraseña actual</span>
                  <input
                    type="password"
                    className="input"
                    value={currentPassword}
                    onChange={event => setCurrentPassword(event.target.value)}
                    autoComplete="current-password"
                  />
                </label>
                <label className="account-form-label">
                  <span>Nueva contraseña</span>
                  <input
                    type="password"
                    className="input"
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <label className="account-form-label">
                  <span>Repetir nueva contraseña</span>
                  <input
                    type="password"
                    className="input"
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <div className="account-form-actions">
                  <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
                    {passwordSaving ? "Guardando..." : "Actualizar contraseña"}
                  </button>
                </div>
              </form>
            </section>

            <section className="panel account-panel">
              <h2 className="panel-title">Suscripción y facturación</h2>
              {snapshot ? (
                <>
                  <p className="panel-subtitle">
                    Plan actual:{" "}
                    <strong>
                      {snapshot.isBypassed
                        ? "Interno"
                        : hasCancelScheduled && isTrialing
                          ? "Prueba activa (baja programada)"
                        : hasCancelScheduled
                          ? "Pro activo (baja programada)"
                        : hasStripeTrialingSubscription
                          ? "Pro activo"
                        : isTrialing
                          ? "Prueba gratuita (30 días con funciones Pro)"
                          : snapshot.plan.name}
                    </strong>
                  </p>
                  {snapshot.subscription?.periodEnd && (
                    <p className="panel-subtitle">
                      {hasStripeTrialingSubscription
                        ? "Primer cobro Pro"
                        : isTrialing
                          ? "Fin de prueba"
                          : "Próxima renovación"}
                      :{" "}
                      {new Date(snapshot.subscription.periodEnd).toLocaleDateString("es-ES")}
                    </p>
                  )}
                </>
              ) : (
                <p className="panel-subtitle">Cargando estado de facturación...</p>
              )}

              <div className="account-billing-actions">
                {hasStripeProSubscription ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={portalLoading}
                      onClick={() => void handleOpenPortal("manage")}
                    >
                      {portalLoading ? "Abriendo portal..." : "Abrir área privada de facturación"}
                    </button>
                    {!hasCancelScheduled && (
                      <button
                        type="button"
                        className="btn btn-light account-billing-cancel-btn"
                        disabled={portalLoading}
                        onClick={() => void handleOpenPortal("cancel")}
                      >
                        {portalLoading ? "Abriendo portal..." : "Solicitar baja de Pro"}
                      </button>
                    )}
                  </>
                ) : isFreePlan || isTrialing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={upgradeLoading !== null}
                      onClick={() => void handleUpgrade("month")}
                    >
                      {upgradeLoading === "month" ? "Abriendo..." : "Pasar a Pro mensual (2,99 €/mes)"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={upgradeLoading !== null}
                      onClick={() => void handleUpgrade("year")}
                    >
                      {upgradeLoading === "year" ? "Abriendo..." : "Pasar a Pro anual (29 €/año)"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={portalLoading || isBypassed}
                    onClick={() => void handleOpenPortal("manage")}
                  >
                    {portalLoading ? "Abriendo portal..." : "Abrir área privada de facturación"}
                  </button>
                )}
              </div>

              <p className="account-help-text">
                {hasCancelScheduled
                  ? "Tu baja está programada. Mantendrás acceso hasta la fecha indicada y luego pasarás automáticamente a Free."
                  : hasStripeProSubscription && isTrialing
                  ? "Ya tienes Pro activado y método de pago asociado. Si lo necesitas, puedes solicitar la baja desde el portal."
                  : isTrialing
                    ? "Durante la prueba tienes todas las funciones Pro. Activa Pro para mantenerlas al terminar los 30 días."
                  : "Cuando actives Pro podrás gestionar pagos, facturas y baja desde tu área de facturación."}
              </p>
              {isBypassed && (
                <p className="account-help-text">
                  Usuario interno: no requiere gestión de facturación.
                </p>
              )}
            </section>

            <section className="panel account-panel account-panel--danger">
              <h2 className="panel-title">Privacidad y datos</h2>
              <p className="panel-subtitle">
                Si lo necesitas, puedes borrar todos tus datos financieros y mantener solo tu acceso.
              </p>
              <div className="account-danger-actions">
                <button
                  type="button"
                  className="btn btn-secondary account-danger-button"
                  onClick={() => setPurgeModalOpen(true)}
                >
                  Borrar todos mis datos
                </button>
              </div>
              <p className="account-help-text">
                Esta acción es irreversible y eliminará movimientos, cuentas, tarjetas, categorías, reglas e importaciones.
              </p>
            </section>
          </div>
        </div>
      </div>

      {purgeModalOpen && (
        <div className="settings-drawer-container settings-drawer-container--modal" role="dialog" aria-modal="true" aria-labelledby="purge-data-title">
          <button
            type="button"
            className="settings-drawer-backdrop settings-drawer-backdrop--soft"
            onClick={() => !purgeLoading && setPurgeModalOpen(false)}
            aria-label="Cerrar modal"
          />
          <aside className="settings-drawer settings-drawer--modal account-purge-modal">
            <button
              type="button"
              className="settings-drawer-close"
              onClick={() => !purgeLoading && setPurgeModalOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <p className="settings-drawer-label">Acción irreversible</p>
            <h3 id="purge-data-title" className="settings-drawer-title">Borrar todos tus datos</h3>
            <p className="settings-drawer-subtitle">
              Para continuar, confirma con tu contraseña actual o escribe exactamente: <strong>BORRAR TODOS MIS DATOS</strong>.
            </p>

            <form className="settings-drawer-form" onSubmit={handlePurgeData}>
              <label className="account-form-label">
                <span>Contraseña actual (opcional si usas texto de confirmación)</span>
                <input
                  type="password"
                  className="input"
                  value={purgeCurrentPassword}
                  onChange={event => setPurgeCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                />
              </label>

              <label className="account-form-label">
                <span>Texto de confirmación</span>
                <input
                  type="text"
                  className="input"
                  value={purgeConfirmText}
                  onChange={event => setPurgeConfirmText(event.target.value)}
                  placeholder="BORRAR TODOS MIS DATOS"
                  autoComplete="off"
                />
              </label>

              <div className="settings-drawer-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPurgeModalOpen(false)}
                  disabled={purgeLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary account-purge-submit"
                  disabled={purgeLoading || !canSubmitPurge}
                >
                  {purgeLoading ? "Borrando..." : "Sí, borrar todos mis datos"}
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </DashboardLayout>
  );
}
